import {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'

export function NavegacionTarjeta({onAtras, onAdelante}: {onAtras: () => void; onAdelante: () => void}) {
  const {t} = useTranslation()
  return (
    <span className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 md:min-h-0"
        data-testid="cola-atras"
        aria-keyshortcuts="ArrowLeft"
        onClick={onAtras}>
        {t('admin.bateria.cola.atras')}
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 md:min-h-0"
        data-testid="cola-adelante"
        aria-keyshortcuts="ArrowRight"
        onClick={onAdelante}>
        {t('admin.bateria.cola.adelante')}
      </Button>
    </span>
  )
}

/**
 * Número posicional sobre la lista congelada, el mismo que mueven `←` y `→`. El salto no es un formulario:
 * `Enter` salta y un valor fuera de rango no mueve la pantalla.
 */
export function PosicionTarjeta({
  numero,
  total,
  enRonda,
  onIr,
  porMarcar,
  onSiguientePorMarcar,
}: {
  numero: number
  total: number
  /** La ronda de repaso: el número cuenta sobre las que se dejaron para después, no sobre la sección. */
  enRonda: boolean
  onIr: (numero: string) => boolean
  porMarcar: number
  onSiguientePorMarcar: () => boolean
}) {
  const {t} = useTranslation()
  const [texto, setTexto] = useState('')
  const caja = useRef<HTMLInputElement>(null)

  function saltar() {
    if (!onIr(texto)) return
    setTexto('')
    // Los atajos se sueltan dentro de una caja de texto: el foco vuelve a la tarjeta.
    caja.current?.blur()
  }

  return (
    <span className="ml-auto flex items-center gap-2">
      <span className="font-mono text-[11px] tabular-nums text-muted-foreground" data-testid="cola-posicion">
        {t(enRonda ? 'admin.bateria.cola.posicion.enRonda' : 'admin.bateria.cola.posicion.deSeccion', {
          numero,
          total,
        })}
      </span>
      {/* Decidir mueve +1; el salto al trabajo que falta es explícito: navega el usuario, no el sistema. */}
      <Button
        size="xs"
        variant="ghost"
        data-testid="cola-siguiente-por-marcar"
        aria-keyshortcuts="P"
        disabled={porMarcar === 0}
        onClick={onSiguientePorMarcar}>
        {porMarcar === 0
          ? t('admin.bateria.cola.porMarcar.ninguna')
          : t('admin.bateria.cola.porMarcar.siguiente', {count: porMarcar})}
      </Button>
      <input
        ref={caja}
        type="text"
        inputMode="numeric"
        value={texto}
        data-testid="cola-ir-a"
        aria-label={t('admin.bateria.cola.posicion.irA', {total})}
        placeholder={t('admin.bateria.cola.posicion.irACorto')}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          e.preventDefault()
          saltar()
        }}
        className="w-16 rounded-md border border-input bg-background px-2 py-1 font-mono text-[11px] text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
      />
    </span>
  )
}
